import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  Client,
  FileParameter,
  OnePInitiativeStageUpdateDTO,
  UpdateInitiativeStageCommand,
} from '../../../api/api-clientInformation';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-confirm-action-dialog',
  standalone: false,
  templateUrl: './confirm-action-dialog.component.html',
  styleUrl: './confirm-action-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmActionDialogComponent{
  apiClient = inject(Client);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  selectedFile?: File;
  actionName = '';
  isLoading = false;
  descriptionFormControl = new FormControl('', [Validators.required, this.noWhitespaceValidator]);

  constructor(
    private toastrService: ToastrService,
    private dialogRef: MatDialogRef<ConfirmActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.actionName = data.actionName;
  }

  private noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    return typeof control.value === 'string' && control.value.length > 0 && !control.value.trim()
      ? { required: true }
      : null;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      const maxSizeInMB = 50; // 50 MB
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (this.selectedFile.size > maxSizeInBytes) {
        this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FILE_SIZE_EXCEEDS', { maxSize: maxSizeInMB }));
        (event.target as HTMLInputElement).value = '';
        this.selectedFile = undefined;
      }
      this.cdr.markForCheck();
    }
  }

  onConfirm(): void {
    if (this.descriptionFormControl.invalid) {
      this.descriptionFormControl.markAsTouched();
      return;
    }

    // Bulk mode: return collected data to the component — no API calls here
    if (this.data.isBulk) {
      this.dialogRef.close({
        success: true,
        comment: this.descriptionFormControl.value,
        file: this.selectedFile,
      });
      return;
    }

    // Single-record mode: existing behavior unchanged
    this.isLoading = true;
    this.descriptionFormControl.disable();
    this.cdr.markForCheck();

    if (this.selectedFile) {
      this.apiClient
        .uploadFileAttachment(this.data.initiativeId,undefined, {
          data: this.selectedFile,
          fileName: this.selectedFile?.name,
        } as FileParameter)
        .subscribe({
          next: (res) => {
            //change stage with attachment and comment
            const onePInitiativeStageUpdateDTO = {
              initiativePk: this.data.initiativeId,
              stage: this.data.stage,
              stageHistoryComment: this.descriptionFormControl.value,
              attachmentFileFk: res
            } as OnePInitiativeStageUpdateDTO;
            const command = {
              onePInitiativeStageUpdateDTO: onePInitiativeStageUpdateDTO,
            } as UpdateInitiativeStageCommand;
            this.apiClient
              .updateInitiativeStage(command)
              .subscribe({
                next: () => {
                  this.isLoading = false;
                  this.dialogRef.close({ success: true });
                },
                error: () => {
                  this.isLoading = false;
                  this.descriptionFormControl.enable();
                  this.cdr.markForCheck();
                  this.dialogRef.close();
                },
              });
          },
          error: () => {
            this.isLoading = false;
            this.descriptionFormControl.enable();
            this.cdr.markForCheck();
            this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FILE_UPLOAD_FAILED'));
          },
        });
    } else {
      const onePInitiativeStageUpdateDTO = {
        initiativePk: this.data.initiativeId,
        stage: this.data.stage,
        stageHistoryComment: this.descriptionFormControl.value,
      } as OnePInitiativeStageUpdateDTO;
      const command = {
        onePInitiativeStageUpdateDTO: onePInitiativeStageUpdateDTO,
      } as UpdateInitiativeStageCommand;

      this.apiClient.updateInitiativeStage(command).subscribe({
        next: () => {
          this.isLoading = false;
          this.dialogRef.close({ success: true });
        },
        error: error => {
          this.isLoading = false;
          this.descriptionFormControl.enable();
          this.cdr.markForCheck();
          this.dialogRef.close();
          this.toastrService.error(JSON.parse(error.response)?.Error|| this.translate.instant('MESSAGES.ERROR.UNEXPECTED_SERVER_ERROR'), this.translate.instant('MESSAGES.ERROR.STAGE_UPDATE_ERROR', { actionName: this.actionName || '' }));
        },
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
